import { motion } from "framer-motion";
import { useEffect, useState, useTransition } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api"; // ✅ use this instead of raw axios

import CandidateListSection from "../components/CandidateListSection";
import DidYouKnowCarousel from "../components/DidYouKnowCarousel";
import ExploreStatesSection from "../components/ExploreStatesSection";
import IndiaMapInteractive from "../components/IndiaMapInteractive";
import LeaderOfTheWeekCard from "../components/LeaderOfTheWeekCard";
import PartyRepresentativesSection from "../components/PartyRepresentativesSection";
import PoliticalPartiesSection from "../components/PoliticalPartiesSection";
import RaiseYourVoiceSection from "../components/RaiseYourVoiceSection";
import VoterEducationSection from "../components/VoterEducationSection";
import WhyRaiseYourVoiceSection from "../components/WhyRaiseYourVoiceSection";
import { getImageUrl } from "../services/api";

const Home = () => {
  const [states, setStates] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [activeTab, setActiveTab] = useState("state");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, startTransition] = useTransition();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async () => {
    try {
      const res = await api.get("/states");
      if (Array.isArray(res.data)) {
        setStates(res.data);
        setFilteredData(res.data.filter((item) => item.type !== "ut"));
      } else {
        throw new Error("Invalid API response");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Failed to fetch state data.");
      setStates([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (type) => {
    startTransition(() => {
      setActiveTab(type);
      const filtered = type === "state"
        ? states.filter((item) => item.type !== "ut")
        : states.filter((item) => item.type === "ut");
      setFilteredData(filtered);
    });
  };

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 bg-orange-600 text-white text-sm text-center py-2 z-50 animate-pulse">
        🇮🇳 भारत की ताकत है आपकी आवाज़ — Raise your voice on JanShakti.in
      </div>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-orange-500 via-white to-green-600 text-gray-900 py-24 px-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.15 } },
          }}
          className="max-w-4xl mx-auto text-center"
        >
          {"जन-जन की आवाज़ — भारत को जागरूक बनाओ 🇮🇳".split(" ").map((word, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="text-5xl font-extrabold leading-tight drop-shadow-md inline-block"
            >
              {word}&nbsp;
            </motion.span>
          ))}
          <p className="mt-4 text-lg text-gray-800 font-medium">
            Desh ke liye bolo — Raise your issue, track leaders, and reform Bharat together.
          </p>
          <div className="mt-6 flex justify-center gap-4">
            <button
              onClick={() => navigate("/issues")}
              className="bg-green-700 text-white px-6 py-2 rounded-full font-semibold hover:bg-green-800 transition"
            >
              ✊ Raise an Issue
            </button>
            <button
              onClick={() => navigate("/register")}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-full font-semibold transition"
            >
              🚀 Join as Voter / Leader
            </button>
          </div>
        </motion.div>
      </div>

      <WhyRaiseYourVoiceSection />

      <div className="bg-white relative z-10 -mt-12 rounded-t-3xl shadow-xl pb-32">
        <div className="max-w-7xl mx-auto px-4 pt-12">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">🗺️ Explore Bharat - States & UTs</h2>
            <div className="flex justify-center gap-4 mt-2">
              <button
                onClick={() => handleTabChange("state")}
                className={`px-6 py-2 rounded-full font-semibold transition ${
                  activeTab === "state" ? "bg-orange-600 text-white" : "bg-gray-200 text-gray-800"
                }`}
              >
                28 States
              </button>
              <button
                onClick={() => handleTabChange("ut")}
                className={`px-6 py-2 rounded-full font-semibold transition ${
                  activeTab === "ut" ? "bg-orange-600 text-white" : "bg-gray-200 text-gray-800"
                }`}
              >
                8 Union Territories
              </button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-1/2 flex justify-center items-start">
              <IndiaMapInteractive />
              <LeaderOfTheWeekCard
                leader={{
                  name: "Alok Kumar Suman",
                  position: "MP",
                  constituency: "Gopalganj",
                  party: "Janata Dal (United)",
                  bio: "Resolved 75+ public issues and promoted school infrastructure upgrades.",
                  image_url: "/image/alok-kumar-suman.png",
                }}
              />
            </div>
            <div className="lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {loading ? (
                <p className="col-span-2 text-blue-600 text-center">Loading...</p>
              ) : error ? (
                <p className="col-span-2 text-red-500 text-center">{error}</p>
              ) : (
                filteredData.map((item, index) => (
                  <motion.div
                    key={index}
                    onClick={() => navigate(`/state/${item.state_name}`)}
                    whileHover={{ scale: 1.03 }}
                    className="bg-white shadow-lg border border-gray-200 p-4 rounded-xl cursor-pointer hover:bg-green-50 transition flex items-center space-x-3"
                  >
                    <img
                      src={getImageUrl(item.party_logo, "party-logos/party.png")}
                      alt={item.state_name}
                      className="w-10 h-10 object-contain"
                      onError={(event) => {
                        event.currentTarget.src = "/party-logos/party.png";
                      }}
                    />
                    <div>
                      <h2 className="font-semibold text-gray-800">{item.state_name}</h2>
                      <p className="text-sm text-gray-500">{item.ruling_party}</p>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </div>

          <PartyRepresentativesSection />
          <PoliticalPartiesSection />
          <VoterEducationSection />
          <CandidateListSection />
          <DidYouKnowCarousel />
          <RaiseYourVoiceSection />
          <ExploreStatesSection />

          <div className="bg-gradient-to-br from-white via-orange-50 to-green-50 rounded-xl border border-orange-200 shadow mt-16 p-8 text-center">
            <h3 className="text-2xl font-bold text-orange-700 mb-2">
              "Be the change you wish to see in the world."
            </h3>
            <p className="text-sm text-gray-600 mb-4 italic">— Mahatma Gandhi</p>
            <button
              onClick={() => navigate("/issues")}
              className="bg-green-700 hover:bg-green-800 text-white px-6 py-3 rounded-full font-semibold"
            >
              🇮🇳 Start Contributing to Bharat
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
